import { alpha, styled } from '@mui/material';
import {
    COLUMN_PADDING_ITEMS,
    ITEM_HEIGHT,
    VIEWPORT_HEIGHT,
} from './constants';
import { padTimeUnit } from './utils';
import { useTimeColumnScroll } from './useTimeColumnScroll';

const PickerColumn = styled('div')({
    position: 'relative',
    width: 76,
});

const PickerViewport = styled('div')(({ theme }) => ({
    'position': 'relative',
    'height': VIEWPORT_HEIGHT,
    'overflowY': 'auto',
    'paddingTop': ITEM_HEIGHT * COLUMN_PADDING_ITEMS,
    'paddingBottom': ITEM_HEIGHT * COLUMN_PADDING_ITEMS,
    'borderRadius': theme.shape.borderRadius * 2,
    'overscrollBehavior': 'contain',
    'scrollbarWidth': 'none',
    'msOverflowStyle': 'none',
    'userSelect': 'none',
    'touchAction': 'pan-y',
    'WebkitOverflowScrolling': 'touch',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
}));

const PickerOverlay = styled('div')(({ theme }) => ({
    position: 'absolute',
    top: ITEM_HEIGHT * COLUMN_PADDING_ITEMS,
    left: 0,
    right: 0,
    height: ITEM_HEIGHT,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    pointerEvents: 'none',
}));

const PickerItem = styled('button', {
    shouldForwardProp: prop => prop !== '$selected',
})<{
    $selected: boolean;
}>(({ theme, $selected }) => ({
    width: '100%',
    height: ITEM_HEIGHT,
    border: 'none',
    background: 'transparent',
    color: $selected
        ? theme.palette.text.primary
        : theme.palette.text.secondary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    outline: 'none',
    fontSize: theme.typography.body1.fontSize,
    fontFamily: theme.typography.fontFamily,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: $selected ? 600 : 400,
}));

export interface TimePickerColumnProps {
    label: string;
    value: number;
    total: number;
    onChange: (value: number) => void;
}

export const TimePickerColumn = ({
    label,
    value,
    total,
    onChange,
}: TimePickerColumnProps) => {
    const {
        handleViewportRef,
        items,
        topSpacerHeight,
        bottomSpacerHeight,
        handleScroll,
        handleKeyDown,
        handleInteractionStart,
        handleItemSelect,
        focusViewport,
    } = useTimeColumnScroll({ value, total, onChange });

    return (
        <PickerColumn>
            <PickerViewport
                ref={handleViewportRef}
                role='listbox'
                aria-label={label}
                tabIndex={0}
                onScroll={handleScroll}
                onKeyDown={handleKeyDown}
                onWheel={handleInteractionStart}
                onPointerDown={handleInteractionStart}
                onTouchStart={handleInteractionStart}
                onClick={focusViewport}>
                <div style={{ height: topSpacerHeight }} />
                {items.map(item => (
                    <PickerItem
                        key={`${label}-${item.rawIndex}`}
                        type='button'
                        role='option'
                        tabIndex={-1}
                        $selected={item.value === value}
                        aria-selected={item.value === value}
                        onMouseDown={event => {
                            event.preventDefault();
                        }}
                        onClick={() => {
                            focusViewport();
                            handleItemSelect(item.rawIndex);
                        }}>
                        {padTimeUnit(item.value)}
                    </PickerItem>
                ))}
                <div style={{ height: bottomSpacerHeight }} />
            </PickerViewport>
            <PickerOverlay />
        </PickerColumn>
    );
};
